import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Volume2 } from "lucide-react";

// Marketing videos served locally from /public/assets for reliable production playback.
const REELS = [
  { title: "Brand Launch Film", client: "Capitol Wellness Co.", src: "/assets/reel-1.mp4#t=0.1" },
  { title: "Social Ad Series", client: "Midtown Auto Spa", src: "/assets/reel-2.mp4#t=0.1" },
  { title: "Campaign Spotlight", client: "RiverCity Realty", src: "/assets/reel-3.mp4#t=0.1" },
];

export const Showreel = () => {
  const [active, setActive] = useState(null);

  return (
    <section data-testid="showreel-section" className="relative py-24 sm:py-32 border-t border-line">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="mono-label text-accent">[ Showreel ]</span>
            <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl text-balance">
              Marketing videos that move people.
            </h2>
          </div>
          <p className="text-text-2 max-w-sm">
            A look at the campaigns and content we've produced for local brands.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {REELS.map((r, i) => (
            <motion.button
              key={r.title}
              type="button"
              onClick={() => setActive(r)}
              data-testid={`showreel-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative block text-left border border-line overflow-hidden accent-glow"
            >
              <div className="relative aspect-video overflow-hidden bg-surface-2">
                <video
                  src={r.src}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-accent/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-ink fill-ink ml-0.5" />
                  </div>
                </div>
              </div>
              <div className="p-5 flex items-center justify-between">
                <div>
                  <div className="font-head font-bold text-lg">{r.title}</div>
                  <div className="mono-label text-text-2 mt-0.5">{r.client}</div>
                </div>
                <Volume2 className="w-4 h-4 text-text-2 group-hover:text-accent transition-colors" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="showreel-modal"
            className="fixed inset-0 z-[100] bg-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="mono-label text-accent">{active.client}</div>
                  <h3 className="font-head font-bold text-2xl">{active.title}</h3>
                </div>
                <button
                  onClick={() => setActive(null)}
                  data-testid="showreel-modal-close"
                  className="w-11 h-11 flex items-center justify-center border border-line text-white hover:border-accent hover:text-accent transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="border border-line bg-black overflow-hidden">
                <video
                  src={active.src}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[75vh] object-contain bg-black"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
